import axios, { AxiosInstance } from 'axios'
import { assign } from 'lodash'

import socket from '@/utils/socket'
import storage from '@/screens/Order/utils/storage.util'

export const REGION_CONFIG = {
  global: {
    publicKey: process.env.REACT_APP_RSA_PUBLIC_KEY,
  },
  europe: {
    publicKey: process.env.REACT_APP_EU_RSA_PUBLIC_KEY,
  },
  australia: {
    publicKey: process.env.REACT_APP_AU_RSA_PUBLIC_KEY,
  },
  'global-china': {
    publicKey: process.env.REACT_APP_GLOB_CHINA_RSA_PUBLIC_KEY,
  },
}

export async function fetchUserRegion(email: string) {
  try {
    const data = await axios.post(
      `${process.env.REACT_APP_API_DISCOVERY_URL}/v1/user-regions/lookup`,
      { email }
    )
    return data?.data?.data
  } catch (error) {
    console.log('ERROR -> fetchUserRegion ', error)
  }
}

export function getCurrentRegion(): any {
  return localStorage.getItem('region') as any
}

export let instance: AxiosInstance

export function setupHttpClient() {
  const region = getCurrentRegion()
  const { apiUrl } = JSON.parse(region)

  const instance = axios.create({
    baseURL: `${apiUrl}/v1`,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: Number(process.env.REACT_APP_AXIOS_TIMEOUT) || 900000,
  })

  instance.interceptors.request.use((config) => {
    const accessToken = localStorage.getItem('access_token')
    const refreshToken = localStorage.getItem('refresh_token')
    const configHeader = {
      headers: {
        ContentType: `application/json`,
        ['user-id']: storage.getUserId(),
        Authorization: `Bearer ${
          config.url != '/auth/refresh' ? accessToken : refreshToken
        }`,
        socketId: socket?.id,
        baseURL: apiUrl,
      },
    }
    assign(config, configHeader)
    return config
  })

  instance.interceptors.response.use(
    (response) => {
      return response
    },
    (error) => {
      const originalRequest = error.config
      if (
        error.response.status === 401 &&
        !originalRequest._retry &&
        originalRequest.url !== '/auth/refresh'
      ) {
        originalRequest._retry = true
        // Use the refresh token to request a new access token
        const refreshToken = localStorage.getItem('refresh_token')
        return instance
          .post(
            '/auth/refresh',
            {},
            {
              headers: {
                Authorization: `Bearer ${refreshToken}`,
              },
            }
          )
          .then((response) => {
            const newAccessToken = response.data.data.accessToken
            const newRefreshToken = response.data.data.newRefreshToken
            localStorage.setItem('access_token', newAccessToken)
            localStorage.setItem('refresh_token', newRefreshToken)
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
            return axios(originalRequest)
          })
          .catch(() => {
            localStorage.removeItem('refresh_token')
            localStorage.removeItem('access_token')
          })
      }
      return Promise.reject(error)
    }
  )

  return instance
}

const HttpRequest = {
  get: (...args: Parameters<AxiosInstance['get']>) =>
    setupHttpClient().get(...args),

  post: (...args: Parameters<AxiosInstance['post']>) =>
    setupHttpClient().post(...args),

  put: (...args: Parameters<AxiosInstance['put']>) =>
    setupHttpClient().put(...args),

  delete: (...args: Parameters<AxiosInstance['delete']>) =>
    setupHttpClient().delete(...args),

  patch: (...args: Parameters<AxiosInstance['patch']>) =>
    setupHttpClient().patch(...args),
}

export default HttpRequest
